import React, { useState, useMemo } from "react";
import Box from "@mui/material/Box";
import {
  Typography,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Grid,
} from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import { DataGrid, GridToolbarContainer, GridToolbarExport } from "@mui/x-data-grid";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import { Page, Text, View, Document, PDFViewer } from "@react-pdf/renderer";
import SearchIcon from "@mui/icons-material/Search";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import DescriptionIcon from "@mui/icons-material/Description";
import EditIcon from "@mui/icons-material/Edit";
import CalculateOutlinedIcon from "@mui/icons-material/CalculateOutlined";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import PaymentIcon from "@mui/icons-material/Payment";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import FileDownloadRoundedIcon from "@mui/icons-material/FileDownloadRounded";
import FilterAltRoundedIcon from "@mui/icons-material/FilterAltRounded";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import DialogContentText from "@mui/material/DialogContentText";
import { useMaterialUIController } from "context";
import { puedeAccion } from "lib/seguridad/permisosPorModulo";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import {
  FP,
  PageHeader,
  StatCard,
  fpDataGridSx,
  fpInputSx,
} from "components/financepro";
import { useApi, useSucursales } from "lib/api";

function formatFechaFacturados(value) {
  if (!value) return "";
  try {
    const date = new Date(value);
    if (isNaN(date.getTime())) {
      const parts = String(value).split("-");
      if (parts.length === 3) {
        return `${parts[2]}-${parts[1]}-${parts[0]}`;
      }
      return String(value);
    }
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  } catch {
    return String(value);
  }
}

function formatMoneyCell(value) {
  if (value == null || value === "") return "0.00";
  return parseFloat(value).toFixed(2);
}

function FacturadosToolbar() {
  return (
    <GridToolbarContainer>
      <GridToolbarExport />
    </GridToolbarContainer>
  );
}

function buildFacturadosColumns(onCancelar) {
  const moneyFmt = (v) => formatMoneyCell(v);
  return [
    { field: "id", headerName: "Préstamo #", width: 110, align: "center", headerAlign: "center" },
    {
      field: "fecha",
      headerName: "Fecha",
      width: 115,
      align: "center",
      headerAlign: "center",
      valueGetter: (value) => formatFechaFacturados(value),
    },
    { field: "nombres", headerName: "Nombres", flex: 1, minWidth: 130 },
    { field: "apellidos", headerName: "Apellidos", flex: 1, minWidth: 130 },
    { field: "chasis", headerName: "Chasis", width: 150 },
    {
      field: "inicial",
      headerName: "Inicial",
      width: 110,
      align: "right",
      headerAlign: "right",
      valueGetter: (value) => moneyFmt(value),
    },
    {
      field: "precio",
      headerName: "Precio",
      width: 110,
      align: "right",
      headerAlign: "right",
      valueGetter: (value) => moneyFmt(value),
    },
    {
      field: "financia",
      headerName: "Financiamiento",
      width: 130,
      align: "right",
      headerAlign: "right",
      valueGetter: (value) => moneyFmt(value),
    },
    {
      field: "interesacobrar",
      headerName: "Interés",
      width: 115,
      align: "right",
      headerAlign: "right",
      valueGetter: (value) => moneyFmt(value),
    },
    ...(onCancelar
      ? [
          {
            field: "acciones",
            headerName: "Acciones",
            width: 100,
            sortable: false,
            filterable: false,
            align: "center",
            headerAlign: "center",
            renderCell: (params) => {
              const financia = parseFloat(params.row?.financia || 0) || 0;
              const interes = parseFloat(params.row?.interesacobrar || 0) || 0;
              if (financia !== 0 || interes !== 0) return null;
              return (
                <Tooltip title="Cancelar factura">
                  <IconButton size="small" color="error" onClick={() => onCancelar(params.row)}>
                    <DeleteOutlineOutlinedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              );
            },
          },
        ]
      : []),
  ];
}

function InformeMotoresFacturados() {
  const [fecha1, setFecha1] = useState("");
  const [fecha2, setFecha2] = useState("");
  const [nosucursal, setNosucursal] = useState("");
  const [tipoFinanciamiento, setTipoFinanciamiento] = useState("Financiamiento");
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [chasisnuevo, setChasisnuevo] = useState("");
  const [chasis] = useState("");
  const [presentarpdf, setPresentarpdf] = useState(false);
  const [loading, setLoading] = useState(false);

  const [controller] = useMaterialUIController();
  const { nombreNegocio, roleDetalles } = controller;
  const { post } = useApi();
  const sucursal = useSucursales();
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down("sm"));
  
  const puedeBorrar = puedeAccion(roleDetalles, "facturacion", "borrar");

  const [confirmCancelOpen, setConfirmCancelOpen] = useState(false);
  const [cancelTarget, setCancelTarget] = useState(null);

  function abrirCancelarFactura(row) {
    setCancelTarget(row);
    setConfirmCancelOpen(true);
  }

  async function cancelarMotorFacturado() {
    if (!cancelTarget) return;
    try {
      const { status, data } = await post("/api/eliminarmotoresfacturados", {
        id: cancelTarget.id,
      });
      if (status === 200) {
        setRows((prev) => prev.filter((r) => r.id !== cancelTarget.id));
        setConfirmCancelOpen(false);
        setCancelTarget(null);
        alert("Factura cancelada correctamente");
      } else {
        alert(data?.Error || "No se pudo cancelar la factura");
        setConfirmCancelOpen(false);
      }
    } catch (err) {
      alert("Error de red: " + err.message);
      setConfirmCancelOpen(false);
    }
  }

  const totalInicial = rows.reduce((sum, row) => sum + (parseFloat(row.inicial) || 0), 0);
  const totalPrecio = rows.reduce((sum, row) => sum + (parseFloat(row.precio) || 0), 0);
  const totalFinanciamiento = rows.reduce((sum, row) => sum + (parseFloat(row.financia) || 0), 0);
  const totalInteres = rows.reduce((sum, row) => sum + (parseFloat(row.interesacobrar) || 0), 0);

  const facturadosColumns = useMemo(
    () => buildFacturadosColumns(puedeBorrar ? abrirCancelarFactura : undefined),
    [puedeBorrar]
  );

  async function procesarrequerimiento(event) {
    if (event && typeof event.preventDefault === "function") {
      event.preventDefault();
    }

    if (!fecha1 || !fecha2) {
      alert("Por favor seleccione ambas fechas");
      return;
    }

    setLoading(true);
    try {
      const { status, data } = await post("/api/informemotoresfacturados", {
        fechadesde: fecha1,
        fechahasta: fecha2,
        sucursal: nosucursal && nosucursal.label ? nosucursal.label.split("-")[0] : "",
        refinanciados: tipoFinanciamiento === "Refinanciamiento",
      });

      if (status === 200) {
        setRows(data["facturacion"] || []);
        return;
      }

      setRows([]);
      if (status === 400) {
        alert(data["Error"] || "Solicitud inválida");
        return;
      }

      alert(data["Error"] || "Error al procesar la solicitud");
    } catch (error) {
      setRows([]);
      alert("Error al procesar la solicitud");
    } finally {
      setLoading(false);
    }
  }

  async function modificarchasis() {
    const { status, data } = await post("/api/modificachasisincautados", {
      chasisacambiar: chasisnuevo,
      chasis: chasis,
    });
    if (status === 200) {
      alert(data["ok"]);
    }
    if (status === 400) {
      alert(data["Error"]);
    }
  }

  const PDF_COLUMNAS = [
    { clave: "id", titulo: "Prest#", ancho: "60px" },
    { clave: "fecha", titulo: "Fecha", ancho: "60px" },
    { clave: "nombres", titulo: "Nombres", ancho: "60px" },
    { clave: "apellidos", titulo: "Apellidos", ancho: "60px" },
    { clave: "chasis", titulo: "Chasis", ancho: "60px" },
    { clave: "inicial", titulo: "Inicial", ancho: "60px" },
    { clave: "precio", titulo: "Precio", ancho: "60px" },
    { clave: "financia", titulo: "Financia", ancho: "60px" },
    { clave: "interesacobrar", titulo: "Interés", ancho: "60px" },
  ];

  const MyDocument = ({ rowsData, nombreNegocio: nombre }) => {
    const totalInicialPDF = rowsData.reduce((sum, row) => sum + (parseFloat(row.inicial) || 0), 0);
    const totalPrecioPDF = rowsData.reduce((sum, row) => sum + (parseFloat(row.precio) || 0), 0);
    const totalFinancia = rowsData.reduce((sum, row) => sum + (parseFloat(row.financia) || 0), 0);
    const totalInteresPDF = rowsData.reduce((sum, row) => sum + (parseFloat(row.interesacobrar) || 0), 0);

    return (
      <PDFViewer style={{ height: isXs ? "calc(100vh - 120px)" : "550px", width: "100%" }}>
        <Document>
          <Page size="A4">
            <View style={{ textAlign: "center", width: "94%", fontSize: 14, textTransform: "uppercase", marginTop: "15px" }} fixed>
              <Text>{nombre || "Empresa"}</Text>
            </View>
            <View style={{ textAlign: "center", width: "94%", fontSize: 9, textTransform: "uppercase" }} fixed>
              <Text>Reporte de Motores Facturados</Text>
            </View>
            <View style={{ textAlign: "center", width: "94%", fontSize: 9, textTransform: "uppercase" }} fixed>
              <Text> </Text>
            </View>
            <View style={{ display: "flex", alignItems: "center", flexDirection: "row", justifyContent: "left", width: "94%", height: "25px", fontSize: 7, backgroundColor: "#1976d2", color: "white", marginLeft: "15px" }} fixed>
              {PDF_COLUMNAS.map((col) => (
                <Text key={col.clave} style={{ width: col.ancho, justifyContent: "left", fontWeight: "bold" }} fixed>
                  {col.titulo}
                </Text>
              ))}
            </View>
            {rowsData.map((filas, i) => (
              <View key={i} style={{ display: "flex", alignItems: "center", flexDirection: "row", justifyContent: "left", width: "94%", height: "15px", fontSize: 6.5, color: "black", marginLeft: "15px", backgroundColor: i % 2 === 0 ? "#f9f9f9" : "white" }}>
                {PDF_COLUMNAS.map((col) => {
                  const raw = filas[col.clave];
                  const texto =
                    col.clave === "inicial" || col.clave === "precio" || col.clave === "financia" || col.clave === "interesacobrar"
                      ? parseFloat(raw || 0).toFixed(2)
                      : raw ?? "";
                  return (
                    <Text key={col.clave} style={{ width: col.ancho, justifyContent: "left" }} fixed>
                      {texto}
                    </Text>
                  );
                })}
              </View>
            ))}
            <Text> </Text>
            <View style={{ display: "flex", alignItems: "center", flexDirection: "row", justifyContent: "left", width: "94%", height: "25px", fontSize: 8, backgroundColor: "#f5f5f5", color: "black", marginLeft: "15px", fontWeight: "bold", borderTop: "2px solid #1976d2" }}>
              {PDF_COLUMNAS.map((col, idx) => {
                if (idx === 0) return <Text key={col.clave} style={{ width: col.ancho, justifyContent: "left" }} fixed>Totales</Text>;
                if (idx >= 1 && idx <= 4) return <Text key={col.clave} style={{ width: col.ancho, justifyContent: "left" }} fixed> </Text>;
                if (col.clave === "inicial") return <Text key={col.clave} style={{ width: col.ancho, justifyContent: "left" }} fixed>{totalInicialPDF.toFixed(2)} </Text>;
                if (col.clave === "precio") return <Text key={col.clave} style={{ width: col.ancho, justifyContent: "left" }} fixed>{totalPrecioPDF.toFixed(2)} </Text>;
                if (col.clave === "financia") return <Text key={col.clave} style={{ width: col.ancho, justifyContent: "left" }} fixed>{totalFinancia.toFixed(2)} </Text>;
                if (col.clave === "interesacobrar") return <Text key={col.clave} style={{ width: col.ancho, justifyContent: "left" }} fixed>{totalInteresPDF.toFixed(2)} </Text>;
                return null;
              })}
            </View>
          </Page>
        </Document>
      </PDFViewer>
    );
  };

  function handlepdf() {
    if (rows.length === 0) {
      alert("No hay datos para procesar este reporte");
    } else {
      setPresentarpdf(true);
    }
  }

  return (
    <Box component="main" sx={{ minHeight: "100vh", bgcolor: FP.color.surface }}>
      <PageHeader
        title="Facturación"
        subtitle="Informe de Motores Facturados"
        cta={
          <Button
            variant="contained"
            startIcon={<FileDownloadRoundedIcon />}
            disabled={rows.length === 0}
            onClick={handlepdf}
            sx={{
              bgcolor: FP.color.navy,
              color: "#FFFFFF",
              borderRadius: 1.4,
              px: 2.2,
              height: 38,
              boxShadow: "none",
              textTransform: "none",
              fontWeight: 700,
              "&:hover": { bgcolor: FP.color.navyDeep, boxShadow: "none" },
              "&.Mui-disabled": { bgcolor: "#E2E8F0", color: "#94A3B8" },
            }}
          >
            Export Data
          </Button>
        }
      />

      <Box sx={{ p: { xs: 2, sm: 2.5, md: 3 } }}>
        <Card
          elevation={0}
          sx={{
            mb: 3,
            borderRadius: 2.5,
            border: `1px solid ${FP.color.border}`,
            bgcolor: FP.color.cardBg,
            boxShadow: FP.shadow.soft,
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              px: { xs: 2, md: 3 },
              py: 1.6,
              borderBottom: `1px solid ${FP.color.border}`,
              bgcolor: "#FAFBFE",
              display: "flex",
              alignItems: "center",
              gap: 1.2,
            }}
          >
            <Box
              sx={{
                width: 30,
                height: 30,
                borderRadius: 1.2,
                bgcolor: FP.color.accentBlueSoft,
                color: FP.color.accentBlue,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <FilterAltRoundedIcon sx={{ fontSize: 18 }} />
            </Box>
            <Typography sx={{ fontWeight: 700, color: FP.color.textPrimary, fontSize: "0.98rem" }}>
              Filtros de Búsqueda
            </Typography>
          </Box>
          <CardContent sx={{ p: { xs: 2, md: 3 } }}>
            <Grid container spacing={2.5}>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  type="date"
                  label="Fecha Desde"
                  value={fecha1}
                  onChange={(event) => setFecha1(event.target.value)}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  sx={fpInputSx}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  type="date"
                  label="Fecha Hasta"
                  value={fecha2}
                  onChange={(event) => setFecha2(event.target.value)}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  sx={fpInputSx}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Autocomplete
                  disablePortal
                  options={sucursal || []}
                  value={nosucursal || null}
                  getOptionLabel={(opt) => (opt && opt.label) || ""}
                  isOptionEqualToValue={(opt, val) => (opt && val && opt.label) === (val && val.label)}
                  onChange={(event, newValue) => setNosucursal(newValue)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Sucursal"
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      sx={fpInputSx}
                    />
                  )}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth sx={fpInputSx}>
                  <InputLabel id="tipo-financiamiento-label" shrink>
                    Tipo Financiamiento
                  </InputLabel>
                  <Select
                    labelId="tipo-financiamiento-label"
                    label="Tipo Financiamiento"
                    notched
                    value={tipoFinanciamiento}
                    onChange={(event) => setTipoFinanciamiento(event.target.value)}
                  >
                    <MenuItem value="">
                      <em>Todos</em>
                    </MenuItem>
                    <MenuItem value="Refinanciamiento">Refinanciamiento</MenuItem>
                    <MenuItem value="Financiamiento">Financiamiento</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
            <Box display="flex" justifyContent="flex-end" sx={{ mt: 2.5 }}>
              <Button
                variant="contained"
                startIcon={<SearchIcon />}
                onClick={() => procesarrequerimiento()}
                disabled={loading}
                sx={{
                  width: { xs: "100%", sm: "auto" },
                  minWidth: { sm: 200 },
                  height: 46,
                  bgcolor: FP.color.navy,
                  color: "#FFFFFF",
                  borderRadius: 1.4,
                  textTransform: "none",
                  fontSize: "0.94rem",
                  fontWeight: 700,
                  boxShadow: "none",
                  "&:hover": { bgcolor: FP.color.navyDeep, boxShadow: "none" },
                  "&.Mui-disabled": { bgcolor: "#CBD5E1", color: "#FFFFFF" },
                }}
              >
                {loading ? "Procesando..." : "Procesar"}
              </Button>
            </Box>
          </CardContent>
        </Card>

        {rows.length > 0 && (
          <Box
            sx={{
              display: "grid",
              width: "100%",
              gap: 2,
              mb: 3,
              gridTemplateColumns: {
                xs: "minmax(0, 1fr)",
                sm: "repeat(2, minmax(0, 1fr))",
                md: "repeat(3, minmax(0, 1fr))",
              },
            }}
          >
            <StatCard
              label="Total de registros"
              value={String(rows.length)}
              icon={DescriptionIcon}
              accent={FP.color.accentBlue}
            />
            <StatCard
              label="Total inicial"
              value={`$${totalInicial.toLocaleString("es-DO", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
              icon={PaymentIcon}
              accent={FP.color.info}
            />
            <StatCard
              label="Total precio"
              value={`$${totalPrecio.toLocaleString("es-DO", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
              icon={AttachMoneyIcon}
              accent={FP.color.navy}
            />
            <StatCard
              label="Total financiamiento"
              value={`$${totalFinanciamiento.toLocaleString("es-DO", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
              icon={CheckCircleIcon}
              accent={FP.color.successDark}
            />
            <StatCard
              label="Total interés"
              value={`$${totalInteres.toLocaleString("es-DO", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
              icon={TrendingUpIcon}
              accent={FP.color.warning}
            />
            <StatCard
              label="Promedio por registro"
              value={`$${(rows.length > 0 ? totalFinanciamiento / rows.length : 0).toLocaleString("es-DO", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
              icon={CalculateOutlinedIcon}
              accent={FP.color.textSecondary}
            />
          </Box>
        )}

        <Card
          elevation={0}
          sx={{
            borderRadius: 2.5,
            border: `1px solid ${FP.color.border}`,
            bgcolor: FP.color.cardBg,
            boxShadow: FP.shadow.soft,
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              px: { xs: 2, md: 3 },
              py: 1.6,
              borderBottom: `1px solid ${FP.color.border}`,
              bgcolor: "#FAFBFE",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1,
              flexWrap: "wrap",
            }}
          >
            <Typography sx={{ fontWeight: 700, color: FP.color.textPrimary, fontSize: "0.98rem" }}>
              Listado de motores facturados
            </Typography>
            {rows.length > 0 && (
              <Button
                variant="outlined"
                size="small"
                startIcon={<PictureAsPdfIcon />}
                onClick={() => handlepdf()}
                sx={{
                  borderColor: FP.color.danger,
                  color: FP.color.danger,
                  borderRadius: 1.2,
                  textTransform: "none",
                  fontWeight: 700,
                  "&:hover": { bgcolor: FP.color.dangerSoft, borderColor: FP.color.danger },
                }}
              >
                Generar PDF
              </Button>
            )}
          </Box>
          <CardContent sx={{ p: { xs: 1.5, sm: 2 }, "&:last-child": { pb: { xs: 1.5, sm: 2 } } }}>
            {rows.length > 0 ? (
              <>
                <Box sx={{ display: { xs: "none", md: "block" }, width: "100%", overflowX: "auto" }}>
                  <DataGrid
                    rows={rows}
                    columns={facturadosColumns}
                    getRowId={(row) => (row.id != null ? String(row.id) : `${row.chasis}-${row.fecha}`)}
                    slots={{ toolbar: FacturadosToolbar }}
                    pageSizeOptions={[5, 10, 25, 50]}
                    initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
                    checkboxSelection
                    disableRowSelectionOnClick
                    rowHeight={42}
                    sx={{ minWidth: { xs: 900, sm: "100%" }, height: 520, ...fpDataGridSx }}
                    autoHeight={false}
                  />
                </Box>
                <Box sx={{ display: { xs: "flex", md: "none" }, flexDirection: "column", gap: 1.4 }}>
                  {rows.map((row, idx) => (
                    <Box
                      key={row.id != null ? String(row.id) : `card-${idx}`}
                      sx={{
                        border: `1px solid ${FP.color.border}`,
                        borderRadius: 2,
                        p: 1.5,
                        bgcolor: "#FFFFFF",
                        boxShadow: FP.shadow.soft,
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 1, mb: 1 }}>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography sx={{ fontWeight: 800, color: FP.color.textPrimary, fontSize: "1rem", lineHeight: 1.2 }}>
                            Préstamo #{row.id}
                          </Typography>
                          <Typography sx={{ color: FP.color.textPrimary, fontSize: "0.85rem", fontWeight: 600, mt: 0.25, wordBreak: "break-word" }}>
                            {[row.nombres, row.apellidos].filter(Boolean).join(" ") || "—"}
                          </Typography>
                          <Typography sx={{ color: FP.color.textSecondary, fontSize: "0.75rem", mt: 0.1 }}>
                            {row.fecha}
                          </Typography>
                        </Box>
                      </Box>
                      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0,1fr))", gap: 1 }}>
                        {[
                          ["Precio", row.precio],
                          ["Inicial", row.inicial],
                          ["Financia", row.financia],
                          ["Int. cobrar", row.interesacobrar],
                        ].map(([label, value]) => (
                          <Box key={label}>
                            <Typography sx={{ color: FP.color.textSecondary, fontSize: "0.66rem", fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase" }}>
                              {label}
                            </Typography>
                            <Typography sx={{ color: FP.color.textPrimary, fontSize: "0.92rem", fontWeight: 600, fontVariantNumeric: "tabular-nums", wordBreak: "break-word" }}>
                              {value ?? "—"}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                      {row.chasis ? (
                        <Box sx={{ mt: 1, pt: 1, borderTop: `1px solid ${FP.color.border}` }}>
                          <Typography sx={{ color: FP.color.textSecondary, fontSize: "0.66rem", fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase" }}>
                            Chasis
                          </Typography>
                          <Typography sx={{ color: FP.color.textPrimary, fontSize: "0.82rem", fontWeight: 600, wordBreak: "break-all" }}>
                            {row.chasis}
                          </Typography>
                        </Box>
                      ) : null}
                    </Box>
                  ))}
                </Box>
              </>
            ) : (
              <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" py={8}>
                <DescriptionIcon sx={{ fontSize: 56, color: FP.color.textMuted, mb: 2 }} />
                <Typography sx={{ color: FP.color.textPrimary, fontWeight: 700 }} gutterBottom>
                  No hay datos para mostrar
                </Typography>
                <Typography sx={{ color: FP.color.textSecondary, fontSize: "0.9rem" }} textAlign="center" px={2}>
                  Seleccione los filtros y haga clic en &quot;Procesar&quot; para obtener resultados.
                </Typography>
              </Box>
            )}
          </CardContent>
        </Card>

        {presentarpdf && (
          <Dialog
            open={presentarpdf}
            maxWidth="lg"
            fullWidth
            fullScreen={isXs}
            onClose={() => setPresentarpdf(false)}
            PaperProps={{ sx: { borderRadius: { xs: 0, sm: 2 } } }}
          >
            <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <Typography variant="h6" fontWeight="bold">
                Vista Previa del Reporte PDF
              </Typography>
              <Button onClick={() => setPresentarpdf(false)} sx={{ minWidth: "auto", p: 1 }}>
                <CancelIcon />
              </Button>
            </DialogTitle>
            <DialogContent>
              <MyDocument rowsData={rows} nombreNegocio={nombreNegocio} />
            </DialogContent>
          </Dialog>
        )}

        <Dialog
          open={open}
          onClose={() => setOpen(false)}
          maxWidth="sm"
          fullWidth
          PaperProps={{ sx: { borderRadius: 2 } }}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
        >
          <DialogTitle id="alert-dialog-title" sx={{ fontWeight: 700 }}>
            <Box display="flex" alignItems="center">
              <EditIcon sx={{ mr: 1, color: FP.color.accentBlue }} />
              Modificar Chasis
            </Box>
          </DialogTitle>
          <DialogContent>
            <Typography sx={{ color: FP.color.textSecondary, mb: 2, fontSize: "0.9rem" }} gutterBottom>
              Digite el nuevo chasis para el registro seleccionado:
            </Typography>
            <TextField
              label="Nuevo Chasis"
              variant="outlined"
              fullWidth
              value={chasisnuevo}
              onChange={(event) => setChasisnuevo(event.target.value)}
              sx={{ mt: 2, ...fpInputSx }}
            />
          </DialogContent>
          <DialogActions sx={{ p: 2, pt: 0 }}>
            <Button
              variant="outlined"
              onClick={() => setOpen(false)}
              startIcon={<CancelIcon />}
              sx={{ textTransform: "none", borderRadius: 1.2 }}
            >
              Cancelar
            </Button>
            <Button
              variant="contained"
              onClick={() => modificarchasis()}
              startIcon={<CheckCircleIcon />}
              sx={{
                textTransform: "none",
                borderRadius: 1.2,
                bgcolor: FP.color.navy,
                "&:hover": { bgcolor: FP.color.navyDeep },
              }}
            >
              Aceptar
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={confirmCancelOpen}
          onClose={() => setConfirmCancelOpen(false)}
          maxWidth="xs"
          fullWidth
          PaperProps={{ sx: { borderRadius: 2 } }}
        >
          <DialogTitle sx={{ fontWeight: 800, color: FP.color.textPrimary }}>
            Cancelar factura
          </DialogTitle>
          <DialogContent>
            <DialogContentText>
              ¿Seguro que deseas cancelar la factura del chasis{" "}
              <strong>{cancelTarget?.chasis || ""}</strong> (Préstamo #{cancelTarget?.id || ""})?
              Esta acción pondrá precio, financiamiento e inicial en 0.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setConfirmCancelOpen(false)}>No</Button>
            <Button color="error" variant="contained" onClick={cancelarMotorFacturado}>
              Sí, cancelar
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Box>
  );
}
export default InformeMotoresFacturados;
